import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/db';
import SafeIcon from '@/common/SafeIcon';

// ─── Dark mode hook ───────────────────────────────────────────────────────────
function useDarkMode() {
  const [dark, setDark] = useState(() => {
    try {
      const stored = localStorage.getItem('reviewfilter_theme');
      if (stored) return stored === 'dark';
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch { return false; }
  });

  useEffect(() => {
    if (dark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('reviewfilter_theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('reviewfilter_theme', 'light');
    }
  }, [dark]);

  return [dark, setDark];
}

// ─── AdminLayout ──────────────────────────────────────────────────────────────
const AdminLayout = ({ requiredRole }) => {
  const { user, logout, returnToSuperAdmin, hasSuperAdminBackup } = useAuth();
  const navigate = useNavigate();
  const [logoUrl, setLogoUrl] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useDarkMode();

  useEffect(() => {
    api.getConfig().then(cfg => {
      setLogoUrl(cfg.logo_url || '');
    }).catch(() => {});
  }, []);

  // ESC billentyű bezárja a sidebárt mobilon
  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  if (!user) return <Navigate to="/login" />;
  if (requiredRole && user.role !== requiredRole) return <Navigate to="/" />;

  const navigation = user.role === 'superadmin' ? [
    { name: 'Vezérlőpult', href: '/superadmin', icon: 'PieChart' },
    { name: 'Vállalkozások', href: '/superadmin/businesses', icon: 'Briefcase' },
    { name: 'Helyszínek', href: '/superadmin/locations', icon: 'MapPin' },
    { name: 'Felhasználók', href: '/superadmin/users', icon: 'Users' },
    { name: 'E-mail beállítások', href: '/superadmin/smtp', icon: 'Mail' },
    { name: 'E-mail sablonok', href: '/superadmin/email-templates', icon: 'FileText' },
    { name: 'GYIK kezelés', href: '/superadmin/faq', icon: 'HelpCircle' },
    { name: 'Rendszer beállítások', href: '/superadmin/settings', icon: 'Settings' },
  ] : [
    { name: 'Dashboard', href: '/admin', icon: 'BarChart2' },
    { name: 'Értékelhető cégprofilok', href: '/admin/locations', icon: 'MapPin' },
    { name: 'Visszajelzések', href: '/admin/reviews', icon: 'MessageSquare' },
    { name: 'GYIK / Súgó', href: '/admin/faq', icon: 'HelpCircle' },
    { name: 'Beállítások', href: '/admin/settings', icon: 'Settings' },
  ];

  const handleReturnToSuperAdmin = () => {
    const success = returnToSuperAdmin();
    if (success) navigate('/superadmin');
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex transition-colors">

      {/* ─── Mobil overlay ─────────────────────────────────────────────────── */}
      <div
        className={`fixed inset-0 z-20 bg-black/50 md:hidden transition-opacity duration-300 ${
          sidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeSidebar}
        aria-hidden="true"
      />

      {/* ─── Sidebar ───────────────────────────────────────────────────────── */}
      <div className={`
        fixed inset-y-0 left-0 z-30 w-64
        md:relative md:z-auto md:inset-auto
        bg-white dark:bg-gray-800
        border-r border-gray-200 dark:border-gray-700
        flex flex-col
        transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>

        {/* Logo / Fejléc */}
        <div className="h-16 flex items-center justify-between px-5 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="h-9 w-auto object-contain" />
            ) : (
              <>
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <SafeIcon name="Star" className="w-5 h-5 text-white" />
                </div>
                <span className="text-lg font-bold text-gray-900 dark:text-white truncate">SocialCard Review</span>
              </>
            )}
          </div>
          {/* Bezárás gomb mobilon */}
          <button
            onClick={closeSidebar}
            className="md:hidden p-1 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Bezárás"
          >
            <SafeIcon name="X" className="w-5 h-5" />
          </button>
        </div>

        {/* Visszalépés SuperAdminhoz */}
        {hasSuperAdminBackup() && (
          <button
            onClick={handleReturnToSuperAdmin}
            className="mx-3 mt-3 flex items-center gap-2 px-3 py-2 text-xs font-bold text-orange-700 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/50 transition-colors"
          >
            <SafeIcon name="ArrowLeft" className="w-4 h-4" />
            Vissza SuperAdmin fiókba
          </button>
        )}

        {/* Navigáció */}
        <nav className="flex-1 px-4 py-5 space-y-1 overflow-y-auto">
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              end={item.href === '/admin' || item.href === '/superadmin'}
              onClick={closeSidebar}
              className={({ isActive }) =>
                `flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  isActive
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`
              }
            >
              <SafeIcon name={item.icon} className="w-5 h-5 mr-3 flex-shrink-0" />
              {item.name}
            </NavLink>
          ))}
        </nav>

        {/* Felhasználó + dark mode toggle + kijelentkezés */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          {/* Felhasználó */}
          <div className="flex items-center mb-3">
            <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-700 dark:text-blue-300 font-bold text-sm flex-shrink-0">
              {user.email.charAt(0).toUpperCase()}
            </div>
            <div className="ml-3 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{user.email}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                {user.role === 'superadmin' ? 'Super Admin' : 'Admin'}
              </p>
            </div>
          </div>

          {/* Dark mode toggle */}
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="w-full flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors mb-1"
          >
            <SafeIcon name={darkMode ? 'Sun' : 'Moon'} className="w-5 h-5 mr-3 flex-shrink-0" />
            {darkMode ? 'Világos mód' : 'Sötét mód'}
          </button>

          {/* Kijelentkezés */}
          <button
            onClick={logout}
            className="w-full flex items-center px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
          >
            <SafeIcon name="LogOut" className="w-5 h-5 mr-3 flex-shrink-0" /> Kijelentkezés
          </button>
        </div>
      </div>

      {/* ─── Fő tartalom ───────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto min-w-0">

        {/* Mobil top bar hamburgerrel */}
        <div className="md:hidden flex items-center h-14 px-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Menü megnyitása"
          >
            <SafeIcon name="Menu" className="w-6 h-6" />
          </button>
          <span className="ml-3 font-bold text-gray-900 dark:text-white text-sm">SocialCard Review</span>
        </div>

        <main className="p-4 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
